import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { store } from './_store';
import { Provider } from 'react-redux';
import { loadConfig } from './config';
import { resolveBrowserNetworkUrl } from './utils/runtimeUrl';
import './index.css';

function normalizeBaseUrl(value) {
  const raw = String(value || '').trim().replace(/\/+$/, '');
  if (!raw) return '';

  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  if (/^(localhost|127\.0\.0\.1)(:\d+)?(\/.*)?$/i.test(raw)) {
    return `http://${raw}`.replace(/\/+$/, '');
  }

  if (/^[a-z0-9.-]+(?::\d+)?(\/.*)?$/i.test(raw)) {
    return `https://${raw}`.replace(/\/+$/, '');
  }

  return raw;
}

function resolveRuntimeApiBase(runtimeConfig) {
  return normalizeBaseUrl(
    runtimeConfig?.REACT_APP_URL
    || runtimeConfig?.API_BASE_URL
    || process.env.REACT_APP_URL
    || process.env.REACT_APP_API_URL
    || ''
  );
}

function normalizeFetchUrl(input, apiBase) {
  if (typeof input !== 'string') return input;

  const value = input.trim();
  if (!value) return input;

  if (/^https?:\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:')) {
    return resolveBrowserNetworkUrl(value);
  }

  // Fix malformed urls like "honeywell-tm-be...azurewebsites.net/login".
  if (/^[a-z0-9.-]+\.[a-z]{2,}(?::\d+)?\/.*/i.test(value)) {
    return resolveBrowserNetworkUrl(normalizeBaseUrl(value));
  }

  // Fix templated strings where base url was missing at build time.
  if (apiBase && /^(undefined|null)\//i.test(value)) {
    return `${apiBase}/${value.replace(/^(undefined|null)\/+/, '')}`;
  }

  return input;
}

function installFetchUrlNormalizer(apiBase) {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }

  const toHeaders = (value) => {
    try {
      return new Headers(value || undefined);
    } catch {
      return new Headers();
    }
  };

  const sanitizeSimpleMethodHeaders = (method, headersValue) => {
    const normalizedMethod = String(method || '').trim().toUpperCase();
    if (normalizedMethod !== 'GET' && normalizedMethod !== 'HEAD') {
      return headersValue;
    }

    const headers = toHeaders(headersValue);
    headers.delete('Content-Type');
    return headers;
  };

  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    if (typeof input === 'string') {
      const nextInit = init ? { ...init } : undefined;
      const method = nextInit?.method || 'GET';
      if (nextInit) {
        nextInit.headers = sanitizeSimpleMethodHeaders(method, nextInit.headers);
      }
      return nativeFetch(normalizeFetchUrl(input, apiBase), nextInit);
    }

    if (input instanceof Request) {
      const normalizedUrl = normalizeFetchUrl(input.url, apiBase);
      const requestedMethod = String((init && init.method) || input.method || 'GET').toUpperCase();

      if (normalizedUrl === input.url && (requestedMethod !== 'GET' && requestedMethod !== 'HEAD')) {
        return nativeFetch(input, init);
      }

      if (normalizedUrl === input.url && !init) {
        const clonedHeaders = sanitizeSimpleMethodHeaders(requestedMethod, input.headers);
        if (clonedHeaders === input.headers) {
          return nativeFetch(input, init);
        }

        return nativeFetch(new Request(input, {
          headers: clonedHeaders
        }));
      }

      const mergedHeaders = sanitizeSimpleMethodHeaders(
        requestedMethod,
        (init && init.headers) || input.headers
      );

      return nativeFetch(new Request(normalizedUrl, input), {
        ...(init || {}),
        method: requestedMethod,
        headers: mergedHeaders
      });
    }

    return nativeFetch(input, init);
  };
}

// Wait for config.json to load before rendering the app
loadConfig().then((runtimeConfig) => {
  const apiBase = resolveRuntimeApiBase(runtimeConfig);
  installFetchUrlNormalizer(apiBase);

  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <Provider store={store}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Provider>
  );
});
